import { ApiBase } from "@/api/ApiBase";

export interface ParceiroResumo {
  id: number;
  nome: string;
  codigo: string;
  nif: string;
  tipo: "Empresa" | "Particular";
  foto: string | null;
  telefone: string | null;
}

export interface ListaParceirosResponse {
  itens: ParceiroResumo[];
  totalEmpresas: number;
  totalParticulares: number;
}

export interface ParceiroDetalhe {
  id: number;
  nome: string;
  codigo: string;
  nif: string;
  tipo: "Empresa" | "Particular";
  email: string | null;
  telefone: string | null;
  endereco: string | null;
  genero: string;
  foto: string | null;
  canal: string;
  grupo: "Cliente" | "Fornecedor";
  dataNascimento: string | null;
  estadoCivil: string;
  estado: string;
  provincia: string | null;
  pais: { id: number; nome: string; codigo: string } | null;
  criador: { nome: string } | null;
  dataCriacao: string;
}

export interface EditarApagarParceiroResponse {
  msg: string;
  tipo: "sucesso" | "erro";
}

export interface DadosParceiroForm {
  nome: string;
  tipo: "Empresa" | "Particular";
  nif: string;
  email: string;
  telefone: string;
  endereco: string;
  provincia: string;
  genero: string;
  estadoCivil: string;
  dataNascimento: string;
  paisId: number;
  foto: File | null;
}

export const PARCEIROS_POR_PAGINA = 100;

/** Base partilhada por ClienteApi/FornecedorApi — no backend ambos são a
 * mesma entidade Parceiro, distinguida só pelo campo "grupo". */
export abstract class ParceiroApi extends ApiBase {
  protected abstract readonly grupo: "Cliente" | "Fornecedor";

  listar(pagina: number): Promise<ListaParceirosResponse> {
    const chave = this.grupo === "Cliente" ? "clientes" : "fornecedores";
    return this.http
      .put<Record<string, unknown>>(
        `/api/facturacao/parceiro/lista-by-comerciante/${pagina}/${this.grupo}`,
      )
      .then((resposta) => ({
        itens: (resposta[chave] as ParceiroResumo[]) ?? [],
        totalEmpresas: (resposta.totalEmpresas as number) ?? 0,
        totalParticulares: (resposta.totalParticulares as number) ?? 0,
      }));
  }

  obterPorCodigo(codigo: string): Promise<ParceiroDetalhe> {
    return this.http.put<ParceiroDetalhe>(
      `/api/facturacao/parceiro/get-one-by-codigo/${encodeURIComponent(
        codigo,
      )}/${this.grupo}`,
    );
  }

  private construirFormData(
    dados: DadosParceiroForm,
    codigoExistente?: string,
  ): FormData {
    const fd = new FormData();
    if (codigoExistente) fd.append("codigo", codigoExistente);
    fd.append("nome", dados.nome);
    fd.append("tipo", dados.tipo);
    fd.append("grupo", this.grupo);
    fd.append("nif", dados.nif);
    fd.append("email", dados.email);
    fd.append("telefone", dados.telefone);
    fd.append("endereco", dados.endereco);
    fd.append("provincia", dados.provincia);
    fd.append(
      "genero",
      dados.tipo === "Particular" ? dados.genero : "Indefinido",
    );
    fd.append(
      "estadoCivil",
      dados.tipo === "Particular" ? dados.estadoCivil : "Indefinido",
    );
    if (dados.tipo === "Particular" && dados.dataNascimento) {
      fd.append("dataNasc", dados.dataNascimento);
    }
    fd.append("pais.id", String(dados.paisId));
    if (dados.foto) fd.append("fotoParceiro", dados.foto);
    return fd;
  }

  adicionar(dados: DadosParceiroForm): Promise<EditarApagarParceiroResponse> {
    return this.http.postMultipart<EditarApagarParceiroResponse>(
      "/api/facturacao/parceiro/add",
      this.construirFormData(dados),
    );
  }

  editar(
    codigo: string,
    dados: DadosParceiroForm,
  ): Promise<EditarApagarParceiroResponse> {
    return this.http.postMultipart<EditarApagarParceiroResponse>(
      "/api/facturacao/parceiro/edit",
      this.construirFormData(dados, codigo),
    );
  }

  apagar(codigo: string): Promise<EditarApagarParceiroResponse> {
    return this.http.post<EditarApagarParceiroResponse>(
      "/api/facturacao/parceiro/delete",
      { codigo, grupo: this.grupo },
    );
  }
}
